/**
 * @module config
 * @description Centralized configuration for Refero
 *
 * All configuration values are defined here to:
 * - Avoid magic numbers scattered in code
 * - Enable easy environment-based overrides
 * - Provide type safety for configuration
 *
 * Environment variables can override defaults via .env file:
 * - VITE_ZOTERO_API_URL
 * - VITE_API_TIMEOUT
 * - VITE_DEFAULT_LOCALE
 * - VITE_VERBOSE_LOGGING
 *
 * @example
 * import { config, api, ui } from '@/config';
 * const timeout = config.api.timeout;
 * const panelWidth = ui.defaultCollectionsWidth;
 */

import type { NotificationType } from '@types';

// =============================================================================
// ENVIRONMENT HELPERS
// =============================================================================

/**
 * Get environment variable with fallback
 */
function getEnv(key: string, fallback: string): string {
  return import.meta.env[key] ?? fallback;
}

/**
 * Get numeric environment variable with fallback
 */
function getEnvNumber(key: string, fallback: number): number {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
}

/**
 * Get boolean environment variable with fallback
 */
function getEnvBoolean(key: string, fallback: boolean): boolean {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
}

// =============================================================================
// API CONFIGURATION
// =============================================================================

export const api = {
  /** Zotero API base URL (direct mode only) */
  zoteroBaseUrl: getEnv('VITE_ZOTERO_API_URL', 'https://api.zotero.org'),

  /** Zotero API version */
  version: 3,

  /** Default page size for paginated requests */
  defaultPageSize: getEnvNumber('VITE_ITEMS_PAGE_SIZE', 100),

  /** Request timeout in milliseconds */
  timeout: getEnvNumber('VITE_API_TIMEOUT', 30000),

  /** Number of retry attempts for failed requests */
  retryAttempts: 3,

  /** Delay between retries in milliseconds */
  retryDelay: 1000,

  /** Maximum retries with exponential backoff */
  maxRetryDelay: 8000,

  /** Headers to include in all requests */
  defaultHeaders: {
    'Content-Type': 'application/json',
  },

  /**
   * Proxy mode configuration (always enabled)
   * All requests go through Supabase Edge Functions for security
   * API keys are stored server-side in Spectral Cloud Portal
   */
  proxy: {
    /** Proxy mode is always enabled for security */
    enabled: true,

    /** Supabase URL */
    supabaseUrl: getEnv('VITE_SUPABASE_URL', ''),

    /** Supabase anonymous key */
    supabaseAnonKey: getEnv('VITE_SUPABASE_ANON_KEY', ''),

    /** Zotero proxy function name */
    zoteroProxyFunction: 'zotero-proxy',

    /** Portal URL for redirects (login, settings) */
    portalUrl: getEnv('VITE_PORTAL_URL', 'http://localhost:5173'),
  },
} as const;

// =============================================================================
// STORAGE CONFIGURATION
// =============================================================================

export const storage = {
  /** Storage key prefix */
  prefix: 'refero',

  /** IndexedDB database name */
  dbName: 'refero-db',

  /** IndexedDB database version */
  dbVersion: 1,

  /** Use IndexedDB (true) or localStorage (false) */
  useIndexedDB: getEnvBoolean('VITE_USE_INDEXEDDB', true),

  /** Storage keys */
  keys: {
    apiKey: 'api-key',
    selectedLibrary: 'selected-library',
    selectedCollection: 'selected-collection',
    uiPreferences: 'ui-preferences',
    searchHistory: 'search-history',
    locale: 'locale',
  },

  /** Cache configuration */
  cache: {
    /** Default TTL in milliseconds */
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    /** Collections cache TTL */
    collectionsTTL: 10 * 60 * 1000, // 10 minutes
    /** Items cache TTL */
    itemsTTL: 2 * 60 * 1000, // 2 minutes
    /** Maximum cache entries */
    maxEntries: 100,
  },
} as const;

// =============================================================================
// UI CONFIGURATION
// =============================================================================

export const ui = {
  /** Default collections panel width in pixels */
  defaultCollectionsWidth: 250,

  /** Default details panel width in pixels */
  defaultDetailsWidth: 300,

  /** Minimum panel width in pixels */
  minPanelWidth: 200,

  /** Maximum panel width in pixels */
  maxPanelWidth: 500,

  /** Resize handle width in pixels */
  resizeHandleWidth: 4,

  /** Notification display duration in milliseconds by type */
  notificationDuration: {
    success: 3000,
    error: 5000,
    warning: 4000,
    info: 3000,
  } satisfies Record<NotificationType, number>,

  /** Maximum notifications to show at once */
  maxNotifications: 5,

  /** Maximum search history entries */
  maxSearchHistory: 10,

  /** Animation durations in milliseconds */
  animations: {
    fast: 150,
    normal: 200,
    slow: 300,
  },

  /** Z-index scale */
  zIndex: {
    dropdown: 100,
    sticky: 200,
    modalBackdrop: 900,
    modal: 1000,
    contextMenu: 2000,
    notification: 3000,
    loading: 9999,
  },
} as const;

// =============================================================================
// ITEMS CONFIGURATION
// =============================================================================

export const items = {
  /** Default page size for API requests */
  pageSize: getEnvNumber('VITE_ITEMS_PAGE_SIZE', 100),

  /** Maximum items to load at once */
  maxItems: 1000,

  /** Minimum search query length */
  searchMinLength: 2,

  /** Debounce delay for search in milliseconds */
  searchDebounceDelay: 300,

  /** Default sort column */
  defaultSortColumn: 'title' as const,

  /** Default sort direction */
  defaultSortDirection: 'asc' as const,
} as const;

// =============================================================================
// I18N CONFIGURATION
// =============================================================================

export const i18n = {
  /** Default locale */
  defaultLocale: getEnv('VITE_DEFAULT_LOCALE', 'fr') as 'en' | 'fr',

  /** Supported locales */
  supportedLocales: ['en', 'fr'] as const,

  /** Fallback locale when translation is missing */
  fallbackLocale: 'en' as const,

  /** Date format options by locale */
  dateFormats: {
    en: {
      short: { day: '2-digit', month: '2-digit', year: '2-digit' },
      long: { day: 'numeric', month: 'long', year: 'numeric' },
    },
    fr: {
      short: { day: '2-digit', month: '2-digit', year: '2-digit' },
      long: { day: 'numeric', month: 'long', year: 'numeric' },
    },
  } as const,
} as const;

// =============================================================================
// LOGGING CONFIGURATION
// =============================================================================

export const logging = {
  /** Enable verbose logging */
  verbose: getEnvBoolean('VITE_VERBOSE_LOGGING', import.meta.env.DEV),

  /** Log levels to display */
  levels: ['error', 'warn', 'info', 'debug'] as const,

  /** Enable console colors */
  useColors: true,

  /** Log prefix */
  prefix: '[Refero]',

  /** Log scopes colors */
  scopeColors: {
    api: '#5c7cfa',
    store: '#28a745',
    ui: '#ffc107',
    storage: '#17a2b8',
    i18n: '#e83e8c',
  },
} as const;

// =============================================================================
// VALIDATION CONFIGURATION
// =============================================================================

export const validation = {
  /** Maximum title length */
  maxTitleLength: 500,

  /** Maximum abstract length */
  maxAbstractLength: 10000,

  /** Maximum extra field length */
  maxExtraLength: 5000,

  /** Maximum URL length */
  maxUrlLength: 2000,

  /** Maximum creators per item */
  maxCreators: 100,
} as const;

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export const features = {
  /** Enable offline mode (requires IndexedDB) */
  offlineMode: false,

  /** Enable item attachments */
  attachments: false,

  /** Enable item notes */
  notes: false,

  /** Enable collection export */
  collectionExport: true,

  /** Enable BibTeX export */
  bibtexExport: true,

  /** Enable duplicate detection */
  duplicateDetection: true,
} as const;

// =============================================================================
// COMBINED CONFIG EXPORT
// =============================================================================

export const config = {
  api,
  storage,
  ui,
  items,
  i18n,
  logging,
  validation,
  features,
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type Config = typeof config;
export type ApiConfig = typeof api;
export type StorageConfig = typeof storage;
export type UIConfig = typeof ui;
export type ItemsConfig = typeof items;
export type I18nConfig = typeof i18n;
export type LoggingConfig = typeof logging;
export type ValidationConfig = typeof validation;
export type FeaturesConfig = typeof features;

export default config;
